"use client";

import { useTheme } from "@/hooks/useTheme";
import { Toaster as Sonner } from "sonner";
import { useEffect } from "react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();

  // Add data-testid to all sonner toast elements for testing
  useEffect(() => {
    // Helper function to add data-testid to toast elements
    const addTestIdToToasts = (element: Element | Document) => {
      const toasts = element.querySelectorAll('[data-sonner-toast]');
      toasts.forEach((toast) => {
        if (!toast.getAttribute('data-testid')) {
          toast.setAttribute('data-testid', 'sonner-toast');
        }
      });
    };

    // Observe document.body since Sonner creates its toaster container lazily
    // when the first toast is shown, not at component mount time
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // Check if the node itself is a toast
            if (node.hasAttribute?.('data-sonner-toast') && !node.getAttribute('data-testid')) {
              node.setAttribute('data-testid', 'sonner-toast');
            }
            // Find all toast elements within the added node
            addTestIdToToasts(node);
          }
        });
      });
    });

    // Start observing document.body for any added nodes
    observer.observe(document.body, { childList: true, subtree: true });

    // Also check for any existing toasts that might already be in the DOM
    addTestIdToToasts(document);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      data-testid="sonner-toaster"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background/98 group-[.toaster]:backdrop-blur-2xl group-[.toaster]:text-foreground group-[.toaster]:border-border/30 group-[.toaster]:shadow-2xl group-[.toaster]:shadow-black/15 dark:group-[.toaster]:shadow-black/40",
          description: "group-[.toast]:text-muted-foreground/80",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
